import {ReactNode} from "react";
import {Tooltip} from "@mui/material";
import {useIntegrator} from "../../integrator.context";
import {ChangeConstantsCommand} from "../../commands/changeConstants.command";
import {RiftColor, StyleTintProfile} from "../../models/remoteConstants.model";
import {ColorControl} from "../../elements/colorInputLabel.element";
import NumberInput from "../../elements/numberInput.element";
import Collapsible from "../../elements/collapsible";
import {useDebouncedCommit} from "../../utils/useDebouncedCommit";
import {LIVE_PREVIEW_DEBOUNCE_MS} from "../../utils/constants";
import {DEFAULT_STYLES} from "./defaultStyles";

// Editor for RemoteConstantsModel.styles — the visual config mirrored from the
// client's TileSettings/UISettings/UnitSettings ScriptableObjects. A theme
// asset: edits ride the theme overlay + Live Preview. See
// Rift/Documentation/editor_style_settings.md.

// Fog scalars. min/max bound the slider only — the number field remains exact
// and may exceed it (the unbounded noise fields have no hard range in C#).
type FogKey =
    | 'fogStrength' | 'fogDesaturation' | 'fogNoiseScale' | 'fogNoiseSpeed'
    | 'fogSwirl' | 'fogNoiseAmount' | 'fogNoiseBrightness';

const FOG_FIELDS: { key: FogKey; label: string; min: number; max: number; tooltip?: string }[] = [
    {key: 'fogStrength', label: 'Strength', min: 0, max: 1, tooltip: '0 = no tint, 1 = fully replace the sprite with the flat fog color'},
    {key: 'fogDesaturation', label: 'Desaturation', min: 0, max: 1, tooltip: 'How far fogged sprites are pushed toward greyscale'},
    {key: 'fogNoiseScale', label: 'Noise Scale', min: 0, max: 10, tooltip: 'Spatial frequency of the swirl (smaller = larger, softer blobs)'},
    {key: 'fogNoiseSpeed', label: 'Noise Speed', min: 0, max: 2, tooltip: 'Animation speed of the swirl drift'},
    {key: 'fogSwirl', label: 'Swirl', min: 0, max: 2, tooltip: 'How much the noise field curls (0 = plain scroll, higher = liquid swirl)'},
    {key: 'fogNoiseAmount', label: 'Noise Amount', min: 0, max: 1, tooltip: 'How strongly the swirl modulates fog density'},
    {key: 'fogNoiseBrightness', label: 'Noise Brightness', min: 0, max: 1, tooltip: 'How strongly the swirl varies fog brightness'},
];

// Tech tree node states. Each has a fill, an outline and a name colour, laid out as
// three columns under 'Fill' / 'Border' / 'Text' headers so a state reads as one row.
const NODE_STATES: {
    label: string;
    fill: 'lockedNode' | 'unlockedNode' | 'researchedNode' | 'purchasedNode';
    border: 'lockedNodeBorder' | 'unlockedNodeBorder' | 'researchedNodeBorder' | 'purchasedNodeBorder';
    text: 'lockedNodeText' | 'unlockedNodeText' | 'researchedNodeText' | 'purchasedNodeText';
    tooltip: string;
}[] = [
    {label: 'Locked', fill: 'lockedNode', border: 'lockedNodeBorder', text: 'lockedNodeText', tooltip: 'A node the player can\'t reach yet'},
    {label: 'Unlocked', fill: 'unlockedNode', border: 'unlockedNodeBorder', text: 'unlockedNodeText', tooltip: 'A node the player could buy or research now'},
    {label: 'Researched', fill: 'researchedNode', border: 'researchedNodeBorder', text: 'researchedNodeText', tooltip: 'A node currently being researched'},
    {label: 'Purchased', fill: 'purchasedNode', border: 'purchasedNodeBorder', text: 'purchasedNodeText', tooltip: 'A node the player already owns'},
];

// One column per swatch, matching ColorControl's 32px button, shared by the header
// row and every value row so the headings sit over their own column.
const SWATCH_COLS = '32px 32px 32px';

// Option UI categories (client: OptionCategory in OptionUI.cs). Each has a disc tint and
// a corner radius, laid out as two columns under 'Tint' / 'Corner' so a category reads as
// one row — the same shape as NODE_STATES above.
const OPTION_CATEGORIES: {
    label: string;
    tint: 'produceOptionTint' | 'buildingActionOptionTint' | 'tileActionOptionTint' | 'unitActionOptionTint';
    corner: 'produceOptionCornerRadius' | 'buildingActionOptionCornerRadius' | 'tileActionOptionCornerRadius' | 'unitActionOptionCornerRadius';
    tooltip: string;
}[] = [
    {label: 'Produce', tint: 'produceOptionTint', corner: 'produceOptionCornerRadius', tooltip: 'Creates something — a unit from a city, a building on a tile'},
    {label: 'Building Action', tint: 'buildingActionOptionTint', corner: 'buildingActionOptionCornerRadius', tooltip: 'Acts on the building — rush, plunder, upgrade, demolish, capture'},
    {label: 'Tile Action', tint: 'tileActionOptionTint', corner: 'tileActionOptionCornerRadius', tooltip: 'Acts on the tile — road, harvest'},
    {label: 'Unit Action', tint: 'unitActionOptionTint', corner: 'unitActionOptionCornerRadius', tooltip: 'Acts on the unit or its cargo — settle, dive, carry, spell'},
];

// Swatch column then the slider, which needs the remaining width.
const OPTION_COLS = '32px auto';

const COLUMN_HEADER: React.CSSProperties = {fontSize: 12, color: 'var(--muted-text-color)'};

function StylesEditorView() {
    const {state, executeCommand} = useIntegrator();

    // Lazy-seed from the baked defaults so the inputs have values to bind.
    // Deep clone — a shared reference would let edits leak across branches and
    // mutate the DEFAULT_STYLES constant. Display-only until an edit fires a
    // command (mirrors generalEditor.view.tsx's `??=` seeding).
    state.styles ??= structuredClone(DEFAULT_STYLES);
    const s = state.styles!;

    // Build an onChange for a color field: applies the edit through the command's
    // data copy (never the render-time `s`).
    const color = (assign: (d: NonNullable<typeof state.styles>, v: RiftColor) => void) =>
        (v: RiftColor) => executeCommand(new ChangeConstantsCommand(d => assign(d.styles!, v)));

    return (
        <div className={'layout vertical'} style={{flex: 1, minWidth: 300, gap: 8, overflowY: 'auto', padding: 4}}>
            <Section title={'Fog'}>
                <Field label={'Color'} tooltip={'Tint applied to fogged sprites (alpha ignored — use Fog Strength)'}>
                    <ColorControl value={s.tileStyle.fogColor} onChange={color((d, v) => d.tileStyle.fogColor = v)}/>
                </Field>
                {FOG_FIELDS.map(f => (
                    <Field key={f.key} label={f.label} tooltip={f.tooltip}>
                        <SliderNumber
                            value={s.tileStyle[f.key]} min={f.min} max={f.max}
                            onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.tileStyle[f.key] = v))}/>
                    </Field>
                ))}
            </Section>

            <Section title={'Movement'}>
                <TintProfileRows label={'Tile'} value={s.tileStyle.movementHighlight}
                                 onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.tileStyle.movementHighlight = v))}/>
                <TintProfileRows label={'Cloud'} value={s.tileStyle.cloudHighlight}
                                 onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.tileStyle.cloudHighlight = v))}/>
                <TintProfileRows label={'Fog'} value={s.tileStyle.fogHighlight}
                                 onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.tileStyle.fogHighlight = v))}/>
            </Section>
            <Section title={'Territory'}>
                <TintProfileRows label={'Own'} value={s.tileStyle.cityHighlight}
                                 onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.tileStyle.cityHighlight = v))}/>
                </Section>

            <Section title={'UI'}>
                <Field label={'Unset Pip'}><ColorControl value={s.uiStyle.unsetPipColor} onChange={color((d, v) => d.uiStyle.unsetPipColor = v)}/></Field>
                <Field label={'Set Pip'}><ColorControl value={s.uiStyle.setPipColor} onChange={color((d, v) => d.uiStyle.setPipColor = v)}/></Field>
                {/* Reads through DEFAULT_STYLES like Border/Text and the tech tree line:
                    absent from branches authored before it existed, and the client treats
                    a missing key as "keep the baked default" rather than transparent. */}
                <Field label={'Deficit Pip'}><ColorControl value={s.uiStyle.deficitPipColor ?? DEFAULT_STYLES.uiStyle.deficitPipColor!}
                                                           onChange={color((d, v) => d.uiStyle.deficitPipColor = v)}/></Field>
                {/* TODO: hidden until the Unity client consumes these UISettings colors.
                    The data (uiStyle.*) is authored/serialized already; re-enable these
                    rows once the client reads them. See Documentation/editor_style_settings.md.
                <Field label={'Interactable'}><ColorControl value={s.uiStyle.interactableColor} onChange={color((d, v) => d.uiStyle.interactableColor = v)}/></Field>
                <Field label={'Disabled'}><ColorControl value={s.uiStyle.disabledColor} onChange={color((d, v) => d.uiStyle.disabledColor = v)}/></Field>
                <Field label={'Background'}><ColorControl value={s.uiStyle.backgroundColor} onChange={color((d, v) => d.uiStyle.backgroundColor = v)}/></Field>
                <Field label={'Text'}><ColorControl value={s.uiStyle.textColor} onChange={color((d, v) => d.uiStyle.textColor = v)}/></Field>
                <Field label={'Attribute (Info Bar)'}><ColorControl value={s.uiStyle.attributeColorInfoBar} onChange={color((d, v) => d.uiStyle.attributeColorInfoBar = v)}/></Field>
                */}
            </Section>

            <Section title={'Options'}>
                {/* Column headers, emitted as two bare grid children like the tech tree's. */}
                <div/>
                <div style={{display: 'grid', gridTemplateColumns: OPTION_COLS, columnGap: 16}}>
                    <span style={COLUMN_HEADER}>Tint</span>
                    <span style={COLUMN_HEADER}>Corner</span>
                </div>

                {/* Both read through DEFAULT_STYLES: absent from branches authored before
                    they existed, and the client treats absent as "keep the baked default",
                    so the control has to show that same default. Corner radius is a
                    fraction of the disc — 0.5 is a circle, ~0.3 a squircle. */}
                {OPTION_CATEGORIES.map(cat => (
                    <Field key={cat.tint} label={cat.label} tooltip={cat.tooltip}>
                        <div style={{display: 'grid', gridTemplateColumns: OPTION_COLS, columnGap: 16, alignItems: 'center'}}>
                            <ColorControl value={s.uiStyle[cat.tint] ?? DEFAULT_STYLES.uiStyle[cat.tint]!}
                                          onChange={color((d, v) => d.uiStyle[cat.tint] = v)}/>
                            <div className={'layout horizontal center'} style={{gap: 8}}>
                                <SliderNumber
                                    value={s.uiStyle[cat.corner] ?? DEFAULT_STYLES.uiStyle[cat.corner]!}
                                    min={0} max={0.5}
                                    onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.uiStyle[cat.corner] = v))}/>
                            </div>
                        </div>
                    </Field>
                ))}

                {/* Not a category: one extra tint for the cargo option whose passenger
                    currently holds the board, so the player can see which one they picked. */}
                <Field label={'Selected Cargo'} tooltip={'The passenger currently holding the board — its landing tiles are green and its targets ringed'}>
                    <div style={{display: 'grid', gridTemplateColumns: OPTION_COLS, columnGap: 16, alignItems: 'center'}}>
                        <ColorControl value={s.uiStyle.selectedCargoOptionTint ?? DEFAULT_STYLES.uiStyle.selectedCargoOptionTint!}
                                      onChange={color((d, v) => d.uiStyle.selectedCargoOptionTint = v)}/>
                    </div>
                </Field>
            </Section>

            <Section title={'Tech Tree'}>
                {/* Column headers for the fill/border pairs. Emitted as two bare grid
                    children so they drop into Section's label/value columns without a
                    Field's tooltip affordance. */}
                <div/>
                <div style={{display: 'grid', gridTemplateColumns: SWATCH_COLS, columnGap: 16}}>
                    <span style={COLUMN_HEADER}>Fill</span>
                    <span style={COLUMN_HEADER}>Border</span>
                    <span style={COLUMN_HEADER}>Text</span>
                </div>

                {/* Border and Text read through DEFAULT_STYLES: they're absent from
                    branches authored before they existed, and the client treats absent
                    as "keep the baked default" — so the swatch has to show that same
                    default rather than an empty or transparent one. */}
                {NODE_STATES.map(state => (
                    <Field key={state.fill} label={state.label} tooltip={state.tooltip}>
                        <div style={{display: 'grid', gridTemplateColumns: SWATCH_COLS, columnGap: 16}}>
                            <ColorControl value={s.uiStyle[state.fill]}
                                          onChange={color((d, v) => d.uiStyle[state.fill] = v)}/>
                            <ColorControl value={s.uiStyle[state.border] ?? DEFAULT_STYLES.uiStyle[state.border]!}
                                          onChange={color((d, v) => d.uiStyle[state.border] = v)}/>
                            <ColorControl value={s.uiStyle[state.text] ?? DEFAULT_STYLES.uiStyle[state.text]!}
                                          onChange={color((d, v) => d.uiStyle[state.text] = v)}/>
                        </div>
                    </Field>
                ))}

                <Field label={'Line'} tooltip={'Dependency lines and arrowheads. One colour for every line — node fill carries progress'}>
                    <ColorControl value={s.uiStyle.techTreeLine ?? DEFAULT_STYLES.uiStyle.techTreeLine!}
                                  onChange={color((d, v) => d.uiStyle.techTreeLine = v)}/>
                </Field>
                <Field label={'Border Width'} tooltip={'Outline thickness in canvas units (a node is 48 across). Scales with zoom, but never drawn thinner than one screen pixel. 0 removes it'}>
                    <SliderNumber
                        value={s.uiStyle.techTreeNodeBorderWidth ?? DEFAULT_STYLES.uiStyle.techTreeNodeBorderWidth!}
                        min={0} max={4}
                        onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.uiStyle.techTreeNodeBorderWidth = v))}/>
                </Field>
            </Section>

            <Section title={'Score Chart'}>
                <Field label={'Lightness Min'} tooltip={'Lowest OKLab lightness a score chart series may use — raises a very dark team colour until it reads against the panel. OKLab lightness, not HSV value: pure green and pure yellow both have value 1 yet are far too luminous'}>
                    <SliderNumber
                        value={s.uiStyle.chartColorLightnessMin ?? DEFAULT_STYLES.uiStyle.chartColorLightnessMin!}
                        min={0} max={1}
                        onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.uiStyle.chartColorLightnessMin = v))}/>
                </Field>
                <Field label={'Lightness Max'} tooltip={'Highest OKLab lightness a score chart series may use. This is the one that stops a fully saturated team colour glaring as a large fill; a colour already below it passes through untouched'}>
                    <SliderNumber
                        value={s.uiStyle.chartColorLightnessMax ?? DEFAULT_STYLES.uiStyle.chartColorLightnessMax!}
                        min={0} max={1}
                        onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.uiStyle.chartColorLightnessMax = v))}/>
                </Field>
                <Field label={'Chroma Max'} tooltip={'Chroma ceiling for a score chart series, in OKLab units. Roughly 0.13 is a muted fill, 0.2 is close to the authored team saturation; above that adjacent bands vibrate where they meet'}>
                    <SliderNumber
                        value={s.uiStyle.chartColorChromaMax ?? DEFAULT_STYLES.uiStyle.chartColorChromaMax!}
                        min={0} max={0.4}
                        onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.uiStyle.chartColorChromaMax = v))}/>
                </Field>
                <Field label={'Divider Width'} tooltip={'Thickness of the divider between two players\' bands in the share view. Whole numbers read cleanest — a sub-pixel width lands on partial pixel coverage that shifts along the line\'s own slope, which shows as shimmer. 0 removes it'}>
                    <SliderNumber
                        value={s.uiStyle.chartDividerWidth ?? DEFAULT_STYLES.uiStyle.chartDividerWidth!}
                        min={0} max={4}
                        onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.uiStyle.chartDividerWidth = v))}/>
                </Field>
            </Section>

            <Section title={'Unit HP'}>
                <Field label={'Normal HP'}><ColorControl value={s.unitStyle.normalHPColor} onChange={color((d, v) => d.unitStyle.normalHPColor = v)}/></Field>
                <Field label={'Critical HP'}><ColorControl value={s.unitStyle.criticalHPColor} onChange={color((d, v) => d.unitStyle.criticalHPColor = v)}/></Field>
                <Field label={'Melee Attack'} tooltip={'Seconds the melee lunge takes when a unit at range 1 attacks'}>
                    <SliderNumber value={s.unitStyle.meleeAttackDuration ?? DEFAULT_STYLES.unitStyle.meleeAttackDuration!}
                                  min={0} max={2}
                                  onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.unitStyle.meleeAttackDuration = v))}/>
                </Field>
                <Field label={'Projectile Flight'} tooltip={'Seconds the projectile spends in flight on a ranged attack — any building, or any unit with range above 1'}>
                    <SliderNumber value={s.unitStyle.rangedProjectileDuration ?? DEFAULT_STYLES.unitStyle.rangedProjectileDuration!}
                                  min={0} max={2}
                                  onChange={v => executeCommand(new ChangeConstantsCommand(d => d.styles!.unitStyle.rangedProjectileDuration = v))}/>
                </Field>
            </Section>
        </div>
    );
}

// Shared width of every section's label column, so the swatches line up
// vertically across ALL sections (a per-section `max-content` would size each
// grid to its own widest label and they'd drift). Sized to fit the widest
// label ("Noise Brightness"); nudge this one value if a longer label is added.
const LABEL_COL = '8.5rem';

// A collapsible section whose body is a two-column grid with a fixed label
// column, so controls left-align consistently across every section.
function Section(props: { title: string; children: ReactNode }) {
    return (
        <Collapsible defaultOpen={false} title={<p className={'text header'} style={{margin: 0}}>{props.title}</p>}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: `${LABEL_COL} auto`,
                columnGap: 16,
                rowGap: 8,
                alignItems: 'center',
                padding: '8px 4px 12px 22px',
            }}>
                {props.children}
            </div>
        </Collapsible>
    );
}

// One grid row: label cell (column 1) + control cell (column 2). Fragment so the
// two cells participate directly in the parent Section grid.
function Field(props: { label: string; tooltip?: string; children: ReactNode }) {
    return (
        <>
            <div className={'normal-text'} style={{whiteSpace: 'nowrap'}}>
                {props.tooltip
                    ? <Tooltip title={props.tooltip} arrow disableInteractive><span style={{cursor: 'help'}}>{props.label}</span></Tooltip>
                    : <span>{props.label}</span>}
            </div>
            <div className={'layout horizontal center'} style={{gap: 8, justifyContent: 'flex-start'}}>
                {props.children}
            </div>
        </>
    );
}

// Slider + narrow number field for a scalar. The slider is clamped to [min,max];
// the number field is the exact source of truth and may exceed the slider.
// Debounced so a slider drag emits one command per settle (see useDebouncedCommit).
function SliderNumber(props: { value: number; min: number; max: number; onChange(v: number): void }) {
    const [value, set] = useDebouncedCommit(props.value, props.onChange, LIVE_PREVIEW_DEBOUNCE_MS);
    const clamped = Math.max(props.min, Math.min(props.max, value));
    return (
        <>
            <input type={'range'} min={props.min} max={props.max} step={0.01} value={clamped}
                   onChange={e => set(parseFloat(e.target.value))}
                   style={{width: 130}}/>
            <NumberInput value={value} step={'0.01'} displayDecimals={2} style={{width: 56}} onChange={set}/>
        </>
    );
}

// A highlight tint = add/multiply/lerp colors (RGB = color, A = weight). Emits a
// spanning sub-header then three Field rows into the parent grid, so the
// Add/Multiply/Lerp labels line up with every other profile.
function TintProfileRows(props: { label: string; value: StyleTintProfile; onChange(v: StyleTintProfile): void }) {
    const p = props.value;
    return (
        <>
            <div style={{gridColumn: '1 / -1', opacity: 0.7, marginTop: 4}} className={'normal-text'}>{props.label}</div>
            <Field label={'Add'} tooltip={'RGB added to texture, A = weight'}>
                <ColorControl value={p.addColor} onChange={v => props.onChange({...p, addColor: v})}/>
            </Field>
            <Field label={'Multiply'} tooltip={'RGB multiplied with texture, A = weight'}>
                <ColorControl value={p.mulColor} onChange={v => props.onChange({...p, mulColor: v})}/>
            </Field>
            <Field label={'Lerp'} tooltip={'RGB target color to blend toward, A = weight'}>
                <ColorControl value={p.lerpColor} onChange={v => props.onChange({...p, lerpColor: v})}/>
            </Field>
        </>
    );
}

export default StylesEditorView;
